import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Chip, Tooltip } from 'components';
import { CellProps } from 'react-table';
import { ParticipantTag, tagIdsMapSelector } from 'store';
import { useSelector } from 'react-redux';

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    marginRight: theme.marginBase / 2,
    marginTop: theme.marginBase / 2,
  },
  container: {
    marginRight: -theme.marginBase / 2,
    marginTop: -theme.marginBase / 2,

    position: 'relative',
    overflow: 'hidden',
    fontSize: 20,
    maxHeight: 30,
    paddingRight: 30,
  },
  content: {},
  ellipsis: {
    position: 'absolute',
    right: theme.marginBase / 2,
    top: theme.marginBase / 2,
  },
}));

export const TagsListCell = (props: CellProps<any>) => {
  const tagsIdMap = useSelector(tagIdsMapSelector);
  const tagIds = (props.row.original.tags as string[]) || [];
  const rowId = props.row.original.id || `${Math.random()}`;
  const tags = tagIds.map((id) => tagsIdMap[id]).filter(Boolean);
  return <TagsList tags={tags} id={rowId} />;
};

interface Props {
  tags: ParticipantTag[];
  id: string;
}

export const TagsList = ({ tags, id }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const ref = useRef<HTMLDivElement | null>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const [hasEllipsis, setEllipsis] = useState(false);

  const updateEllipsis = useCallback(() => {
    if (!ref.current || !cellRef.current) {
      return;
    }
    const { clientHeight } = ref.current;
    const { clientHeight: outHeight } = cellRef.current;
    if (clientHeight > outHeight) {
      if (!hasEllipsis) {
        setEllipsis(true);
      }
    } else {
      if (hasEllipsis) {
        setEllipsis(false);
      }
    }
  }, [hasEllipsis, setEllipsis, ref, cellRef]);

  useEffect(() => {
    updateEllipsis();
    const id = setTimeout(updateEllipsis, 300); // waiting for animation end
    window.addEventListener('resize', updateEllipsis);
    return () => {
      clearTimeout(id);
      window.removeEventListener('resize', updateEllipsis);
    };
  }, [updateEllipsis, tags]);

  return (
    <Tooltip
      placement="top"
      arrow
      title={
        hasEllipsis
          ? tags.map((tag: ParticipantTag) => (
              <Chip
                customColor={tag.color}
                className={classes.chip}
                key={tag.id}
              >
                {tag.name}
              </Chip>
            ))
          : ''
      }
    >
      <div className={classes.container} ref={cellRef}>
        <div className={classes.content} ref={ref} data-tip data-for={id}>
          {tags.map((tag: ParticipantTag) => (
            <Chip customColor={tag.color} className={classes.chip} key={tag.id}>
              {tag.name}
            </Chip>
          ))}
          {hasEllipsis && (
            <React.Fragment>
              <Chip color="ink" className={classes.ellipsis}>
                ...
              </Chip>
            </React.Fragment>
          )}
        </div>
      </div>
    </Tooltip>
  );
};
